import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext.js";
import api from "../api/api.js";
import MainLayout from "../sidebar/Sidebar.js";

export default function BidComparison() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [bidCards, setBidCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [location, setLocation] = useState("Bid Comparison");

  // Fetch projects for dropdown
  useEffect(() => {
    async function fetchProjects() {
      try {
        const response = await api.get("/bid-comparison/projects");
        setProjects(response.data);
      } catch (err) {
        setError("Failed to load projects.");
      }
    }

    if (user) fetchProjects();
  }, [user]);

  // Fetch bid cards when project is selected
  useEffect(() => {
    async function fetchBidCards() {
      if (!selectedProjectId) {
        setBidCards([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      setBidCards([]); // clear immediately on selection

      // Small delay before fetching data
      await new Promise((res) => setTimeout(res, 300));

      try {
        const response = await api.get(
          `/bid-comparison/projects/${selectedProjectId}/bid-cards`
        );
        setBidCards(response.data);
      } catch (err) {
        setError("Failed to load bid cards.");
        setBidCards([]);
      } finally {
        setLoading(false);
      }
    }

    fetchBidCards();
  }, [selectedProjectId]);

  // Calculate price difference for a bid card
  const calculatePriceDifference = (bidEntries) => {
    if (!bidEntries || bidEntries.length === 0) return 0;

    const prices = bidEntries
      .map((entry) => entry.bid_price)
      .filter((price) => !isNaN(price) && price > 0);

    if (prices.length === 0) return 0;

    const maxPrice = Math.max(...prices);
    const minPrice = Math.min(...prices);
    return maxPrice - minPrice;
  };

  // Add a new empty bid card
  const addNewBidCard = () => {
    const newCard = {
      id: `temp-${Date.now()}`,
      title: "",
      bid_entries: [{ bid_price: "", company_name: "" }],
      isNew: true,
    };
    setBidCards([...bidCards, newCard]);
  };

  // Add a new bid entry row to a card
  const addBidEntry = (cardIndex) => {
    const updatedCards = [...bidCards];
    updatedCards[cardIndex].bid_entries.push({
      bid_price: "",
      company_name: "",
    });
    setBidCards(updatedCards);
  };

  // Update bid card title
  const updateCardTitle = (cardIndex, title) => {
    const updatedCards = [...bidCards];
    updatedCards[cardIndex].title = title;
    setBidCards(updatedCards);
  };

  // Update bid entry
  const updateBidEntry = (cardIndex, entryIndex, field, value) => {
    const updatedCards = [...bidCards];

    // For bid_price, ensure it's numeric only
    if (field === "bid_price") {
      // Remove any non-numeric characters
      const numericValue = value.replace(/[^0-9]/g, "");
      updatedCards[cardIndex].bid_entries[entryIndex][field] = numericValue;
    } else {
      updatedCards[cardIndex].bid_entries[entryIndex][field] = value;
    }

    setBidCards(updatedCards);
  };

  // Save a bid card (for new cards)
  const saveBidCard = async (cardIndex) => {
    if (!selectedProjectId) {
      setError("Please select a project first.");
      return;
    }

    const card = bidCards[cardIndex];
    if (!card.title.trim()) {
      setError("Please enter a title for the bid card.");
      return;
    }

    // Validate bid entries
    const validEntries = card.bid_entries.filter(
      (entry) =>
        entry.company_name.trim() &&
        entry.bid_price &&
        !isNaN(parseInt(entry.bid_price))
    );

    if (validEntries.length === 0) {
      setError(
        "Please add at least one valid bid entry with company name and numeric price."
      );
      return;
    }

    try {
      setLoading(true);
      setError("");

      const bidCardData = {
        title: card.title.trim(),
        bid_entries: validEntries.map((entry) => ({
          bid_price: parseInt(entry.bid_price),
          company_name: entry.company_name.trim(),
        })),
      };

      const response = await api.post(
        `/bid-comparison/projects/${selectedProjectId}/bid-cards`,
        bidCardData
      );

      // Replace the temporary card with the saved one
      const updatedCards = [...bidCards];
      updatedCards[cardIndex] = response.data;
      setBidCards(updatedCards);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save bid card.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div
        className="min-h-full py-4 px-6"
        style={{
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
          {/* Project Selector */}
          <div className="mb-6">
            <label
              htmlFor="project-select"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Select Project
            </label>
            <select
              id="project-select"
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full max-w-md p-3 border border-gray-300 rounded-lg bg-white text-black focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Choose a project...</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.project_code || `PRJ-${project.user_project_number}`}{" "}
                  - {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Loading Display */}
          {loading && (
            <div className="text-gray-500 text-center py-8">Loading...</div>
          )}

          {/* Bid Cards */}
          {selectedProjectId && (
            <div className="space-y-6">
              {bidCards.map((card, cardIndex) => (
                <div key={card.id} className="flex gap-4 items-start">
                  {/* Bid Card */}
                  <div className="flex-1 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    {/* Card Title */}
                    <div className="mb-4">
                      <input
                        type="text"
                        placeholder="Enter bid title..."
                        value={card.title}
                        onChange={(e) =>
                          updateCardTitle(cardIndex, e.target.value)
                        }
                        className="w-full text-black text-lg font-semibold bg-transparent border-b border-gray-300 focus:border-blue-500 focus:outline-none pb-2"
                        disabled={!card.isNew}
                      />
                    </div>

                    {/* Bid Entries */}
                    <div className="space-y-3">
                      {card.bid_entries.map((entry, entryIndex) => (
                        <div
                          key={entryIndex}
                          className="flex gap-3 items-center"
                        >
                          <input
                            type="text"
                            placeholder="Bid Price"
                            value={entry.bid_price}
                            onChange={(e) =>
                              updateBidEntry(
                                cardIndex,
                                entryIndex,
                                "bid_price",
                                e.target.value
                              )
                            }
                            className="w-32 p-2 text-black border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={!card.isNew}
                            pattern="[0-9]*"
                            inputMode="numeric"
                          />
                          <input
                            type="text"
                            placeholder="Company Name"
                            value={entry.company_name}
                            onChange={(e) =>
                              updateBidEntry(
                                cardIndex,
                                entryIndex,
                                "company_name",
                                e.target.value
                              )
                            }
                            className="flex-1 p-2 border text-black border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={!card.isNew}
                          />
                        </div>
                      ))}

                      {/* Add Entry Button */}
                      {card.isNew && (
                        <button
                          onClick={() => addBidEntry(cardIndex)}
                          className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500 transition-colors"
                        >
                          + Add Bid Entry
                        </button>
                      )}
                    </div>

                    {/* Save Button for New Cards */}
                    {card.isNew && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => saveBidCard(cardIndex)}
                          disabled={loading}
                          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded font-medium transition-colors"
                        >
                          {loading ? "Saving..." : "Save Bid Card"}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Price Difference Display */}
                  <div className="w-32 bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
                    <div className="text-sm text-gray-600 mb-1">Price Diff</div>
                    <div className="text-lg font-bold text-gray-900">
                      $
                      {calculatePriceDifference(
                        card.bid_entries
                      ).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}

              {/* Add New Card Button */}
              <button
                onClick={addNewBidCard}
                className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-blue-500 hover:text-blue-500 transition-colors font-medium"
              >
                + Add New Bid Card
              </button>
            </div>
          )}

          {/* Empty State */}
          {selectedProjectId && !loading && bidCards.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500 mb-4">
                No bid cards found for this project.
              </div>
              {/* <button
                onClick={addNewBidCard}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Create First Bid Card
              </button> */}
            </div>
          )}
      </div>
    </MainLayout>
  );
}
